import get from 'lodash/get';
import api from 'services/api';
import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import { selectRowsPerPage } from 'store/Filters/selectors';
import { redirect } from 'store/Router/actions';
import { selectPathname } from 'store/Router/selectors';
import {
  selectExploreGroupPayload,
  selectExploreGroupHostsPage,
  selectExploreGroupServicesPage,
  selectIsExploreGroupOpen
} from './selectors';
import {
  GROUP_VULNS_FETCHING,
  GROUP_VULNS_FAIL,
  GROUP_VULNS_CLEAR_ERROR,
  GROUP_VULNS_CREATE_SUCCESS,
  GROUP_VULNS_ADD_SUCCESS,
  GROUP_VULNS_REMOVE_SUCCESS,
  GROUP_VULNS_DELETE_SUCCESS,
  GROUP_VULNS_RENAME_SUCCESS,
  GROUP_VULNS_EXPLORE_OPEN,
  GROUP_VULNS_EXPLORE_CLOSE,
  GROUP_VULNS_EXPLORE_SET_ACTIVE_TAB,
  GROUP_VULNS_EXPLORE_SET_HOSTS_PAGE,
  GROUP_VULNS_EXPLORE_SET_SERVICES_PAGE,
  GROUP_VULNS_EXPLORE_HOSTS_FETCHING,
  GROUP_VULNS_EXPLORE_HOSTS_SUCCESS,
  GROUP_VULNS_EXPLORE_HOSTS_FAIL,
  GROUP_VULNS_EXPLORE_SERVICES_FETCHING,
  GROUP_VULNS_EXPLORE_SERVICES_SUCCESS,
  GROUP_VULNS_EXPLORE_SERVICES_FAIL,
  GROUP_VULNS_EXPLORE_SHOW_UNGROUP_CONFIRMATION_MODAL,
  GROUP_VULNS_EXPLORE_HIDE_UNGROUP_CONFIRMATION_MODAL,
  GROUP_VULNS_EXPLORE_SET_SELECTED_HOST_IDS,
  GROUP_VULNS_EXPLORE_SET_SELECTED_SERVICE_IDS,
  GROUP_VULNS_EXPLORE_UPDATE_HOST_TAGS,
  GROUP_VULNS_EXPLORE_UPDATE_SERVICE_TAGS,
  GROUP_VULNS_EXPLORE_HOSTS_DELETE_SUCCESS,
  GROUP_VULNS_EXPLORE_SERVICES_DELETE_SUCCESS
} from './constants';

import { getVulnsCountInWs, getHostsCountInWs } from 'Screens/Contextualization/Faraday/actions/Actions';
import { getTags, getHostsTags, getServicesTags } from 'Screens/Contextualization/Tags/actions/Actions';
import { selectCurrentHost } from 'store/Host/selectors';
import { newGetVulns } from 'store/Manage/filterActions';
import { getVulns } from 'store/HostDetail/actions';
import { getVulnsGeneral } from 'store/ManageGeneral/actions';

const fetching = () => ({ type: GROUP_VULNS_FETCHING });
const fail = (e) => ({
  type: GROUP_VULNS_FAIL,
  message: get(e, 'message', 'There was an error, please try again')
});

export const clearError = () => ({ type: GROUP_VULNS_CLEAR_ERROR });

const normalizeEntity = (entity) => entity || 'vulns';
const DEFAULT_EXPLORE_PAGE = 1;
const DEFAULT_EXPLORE_PAGE_SIZE = 50;

const normalizePositiveInteger = (value, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const normalized = Math.trunc(parsed);
  return normalized >= 1 ? normalized : fallback;
};

const getExplorePageSize = (state) => normalizePositiveInteger(
  selectRowsPerPage('vulns', state),
  DEFAULT_EXPLORE_PAGE_SIZE
);

const refreshList = (entity) => async (dispatch, getState) => {
  const state = getState();

  const currentAsset = selectCurrentHost(state);
  const hostIp = get(currentAsset, 'ip', '');

  const refresh = {
    vulns: () => dispatch(newGetVulns()),
    vulnsAssets: () => dispatch(getVulns(hostIp)),
    vulnsGeneral: () => dispatch(getVulnsGeneral())
  };

  const key = normalizeEntity(entity);
  if (refresh[key]) await refresh[key]();

  await dispatch(getVulnsCountInWs());
  await dispatch(getHostsCountInWs());
};

const BACKEND_INVALID_GROUPING_MESSAGE = 'No valid vulnerabilities found. They may not belong to this workspace or are already part of a group.';

const getRowValue = (row) => row?.value ?? row;
const isGrouped = (row) => {
  const value = getRowValue(row);
  return value?.is_automatic !== null && typeof value?.is_automatic !== 'undefined';
};
const isGroupHeader = (row) => {
  const value = getRowValue(row);
  return isGrouped(row) && value?.is_main === true;
};
const isAutomaticGroup = (row) => {
  const value = getRowValue(row);
  return isGroupHeader(row) && value?.is_automatic === true;
};
const exploreRoutePattern = /^\/vulns\/([^/]+)\/groups\/([^/]+)$/;

const getGroupIdFromPath = (pathname) => {
  const match = (pathname || '').match(exploreRoutePattern);
  if (!match) return null;
  const routeGroupId = match[2];
  return routeGroupId === 'new' ? null : routeGroupId;
};

const getRefreshGroupId = (state) => {
  const pathname = selectPathname(state);
  const routeGroupId = getGroupIdFromPath(pathname);
  if (routeGroupId !== null) return routeGroupId;
  const payload = selectExploreGroupPayload(state);
  const payloadGroupId = get(payload, 'groupId', get(payload, 'groupHeaderId', null));
  return payloadGroupId;
};

/**
 * CREATE MANUAL GROUP
 * @param {string} entity - 'vulns' | 'vulnsAssets' | 'vulnsGeneral'
 * @param {number[]} groupIds - ids selected to create the group
 * @param {string} groupTitle - group name
 */
export const createManualGroup = (entity, groupIds, groupTitle) => async (dispatch, getState) => {
  try {
    dispatch(fetching());

    const ws = selectCurrentWorkspace(getState());
    const payload = { groupIds, groupTitle };

    const res = await api.groupVulns.createManualGroup(ws, payload);

    dispatch({ type: GROUP_VULNS_CREATE_SUCCESS });
    await dispatch(refreshList(entity));

    return res;
  } catch (e) {
    dispatch(fail(e));
    return null;
  }
};

/**
 * ADD TO MANUAL GROUP
 * @param {string} entity
 * @param {number} groupHeader - header id (main group vuln)
 * @param {number[]} groupIds - ids to add
 */
export const addToManualGroup = (entity, groupHeader, groupIds) => async (dispatch, getState) => {
  try {
    dispatch(fetching());

    const ws = selectCurrentWorkspace(getState());
    const payload = { groupHeader, groupIds };

    const res = await api.groupVulns.addToManualGroup(ws, payload);

    dispatch({ type: GROUP_VULNS_ADD_SUCCESS });
    await dispatch(refreshList(entity));

    return res;
  } catch (e) {
    dispatch(fail(e));
    return null;
  }
};

/**
 * REMOVE FROM MANUAL GROUP
 * @param {string} entity
 * @param {number[]} groupIds - ids to remove from their group
 */
export const removeFromManualGroup = (entity, groupIds) => async (dispatch, getState) => {
  try {
    dispatch(fetching());

    const ws = selectCurrentWorkspace(getState());
    const payload = { groupIds };

    const res = await api.groupVulns.removeFromManualGroup(ws, payload);

    dispatch({ type: GROUP_VULNS_REMOVE_SUCCESS });
    await dispatch(refreshList(entity));

    return res;
  } catch (e) {
    dispatch(fail(e));
    return null;
  }
};

/**
 * DELETE MANUAL GROUPS
 * @param {string} entity
 * @param {number[]} groupsIds - group header ids to delete
 */
export const deleteManualGroups = (entity, groupsIds) => async (dispatch, getState) => {
  try {
    dispatch(fetching());

    const ws = selectCurrentWorkspace(getState());
    const payload = { groupsIds };

    const res = await api.groupVulns.deleteManualGroups(ws, payload);

    dispatch({ type: GROUP_VULNS_DELETE_SUCCESS });
    await dispatch(refreshList(entity));

    return res;
  } catch (e) {
    dispatch(fail(e));
    return null;
  }
};

/**
 * RENAME MANUAL GROUP
 * @param {string} entity
 * @param {number|string} groupHeaderId - header id (main group vuln)
 * @param {string} newTitle - new group title
 */
export const renameManualGroup = (entity, groupHeaderId, newTitle) => async (dispatch, getState) => {
  try {
    dispatch(fetching());

    const ws = selectCurrentWorkspace(getState());
    const payload = { groupHeaderId, newTitle };

    const res = await api.groupVulns.renameManualGroup(ws, payload);

    dispatch({ type: GROUP_VULNS_RENAME_SUCCESS });
    await dispatch(refreshList(entity));

    return res;
  } catch (e) {
    dispatch(fail(e));
    return null;
  }
};

export const ManualGroupingErrors = {
  MIN_SELECTION: 'MIN_SELECTION',
  MULTIPLE_GROUPS: 'MULTIPLE_GROUPS',
  AUTOMATIC_GROUP: 'AUTOMATIC_GROUP'
};

export const getManualGroupingBlocker = (vulnsSelected = []) => {
  const selected = vulnsSelected ?? [];
  const selectedGroupHeaders = selected.filter(isGroupHeader);

  if (selected.length < 2) {
    return {
      code: ManualGroupingErrors.MIN_SELECTION,
      title: 'Manual Grouping',
      description: 'Please select two or more vulnerabilities to create a manual grouping.'
    };
  }

  if (selectedGroupHeaders.length > 1) {
    return {
      code: ManualGroupingErrors.MULTIPLE_GROUPS,
      title: 'Manual Grouping',
      description: BACKEND_INVALID_GROUPING_MESSAGE
    };
  }

  if (selectedGroupHeaders.length === 1 && isAutomaticGroup(selectedGroupHeaders[0])) {
    return {
      code: ManualGroupingErrors.AUTOMATIC_GROUP,
      title: 'Manual Grouping',
      description: 'Only manual groups can be modified.'
    };
  }

  return null;
};

export const refreshExploreGroupHosts = ({ page, pageSize } = {}) => async (dispatch, getState) => {
  const state = getState();
  const pathname = selectPathname(state);
  const isExploreRoute = exploreRoutePattern.test(pathname || '');
  const isExploreOpen = selectIsExploreGroupOpen(state);

  if (!isExploreRoute && !isExploreOpen) return null;

  const ws = selectCurrentWorkspace(state);
  const groupId = getRefreshGroupId(state);
  const currentPage = selectExploreGroupHostsPage(state);
  const resolvedPage = normalizePositiveInteger(page, currentPage);
  const resolvedPageSize = normalizePositiveInteger(pageSize, getExplorePageSize(state));
  if (!ws) return null;

  try {
    dispatch({ type: GROUP_VULNS_EXPLORE_HOSTS_FETCHING });

    const showGroupHostsPayload = (groupId === null || typeof groupId === 'undefined')
      ? { page: resolvedPage, pageSize: resolvedPageSize }
      : { groupId, page: resolvedPage, pageSize: resolvedPageSize };
    const res = await api.groupVulns.showGroupHosts(ws, showGroupHostsPayload);
    const hosts = get(res, 'rows', get(res, 'hosts', get(res, 'results', get(res, 'data', []))));
    const hostsArray = Array.isArray(hosts) ? hosts : [];
    const hostCount = get(res, 'count', hostsArray.length);
    dispatch({
      type: GROUP_VULNS_EXPLORE_HOSTS_SUCCESS,
      hosts: hostsArray,
      meta: {
        ws,
        groupId,
        hostCount,
        page: resolvedPage,
        pageSize: resolvedPageSize
      }
    });
    return res;
  } catch (e) {
    dispatch({ type: GROUP_VULNS_EXPLORE_HOSTS_FAIL });
    dispatch(fail(e));
    return null;
  }
};

export const refreshExploreGroupServices = ({ page, pageSize } = {}) => async (dispatch, getState) => {
  const state = getState();
  const pathname = selectPathname(state);
  const isExploreRoute = exploreRoutePattern.test(pathname || '');
  const isExploreOpen = selectIsExploreGroupOpen(state);

  if (!isExploreRoute && !isExploreOpen) return null;

  const ws = selectCurrentWorkspace(state);
  const groupId = getRefreshGroupId(state);
  const currentPage = selectExploreGroupServicesPage(state);
  const resolvedPage = normalizePositiveInteger(page, currentPage);
  const resolvedPageSize = normalizePositiveInteger(pageSize, getExplorePageSize(state));
  if (!ws) return null;

  try {
    dispatch({ type: GROUP_VULNS_EXPLORE_SERVICES_FETCHING });

    const showGroupServicesPayload = (groupId === null || typeof groupId === 'undefined')
      ? { page: resolvedPage, pageSize: resolvedPageSize }
      : { groupId, page: resolvedPage, pageSize: resolvedPageSize };
    const res = await api.groupVulns.showGroupServices(ws, showGroupServicesPayload);
    const services = get(res, 'services', get(res, 'rows', get(res, 'results', get(res, 'data', []))));
    const servicesArray = Array.isArray(services) ? services : [];
    const serviceCount = get(res, 'count', servicesArray.length);
    dispatch({
      type: GROUP_VULNS_EXPLORE_SERVICES_SUCCESS,
      services: servicesArray,
      meta: {
        ws,
        groupId,
        serviceCount,
        page: resolvedPage,
        pageSize: resolvedPageSize
      }
    });
    return res;
  } catch (e) {
    dispatch({ type: GROUP_VULNS_EXPLORE_SERVICES_FAIL });
    dispatch(fail(e));
    return null;
  }
};

export const exploreGroup = (payload) => (dispatch, getState) => {
  const ws = selectCurrentWorkspace(getState());
  const groupId = get(payload, 'groupId', null);
  const groupHeaderId = get(payload, 'groupHeaderId', null);

  // Debug aid: inspect from browser console via window.__lastExploreGroupPayload
  if (typeof window !== 'undefined') window.__lastExploreGroupPayload = payload;
  // eslint-disable-next-line no-console
  console.warn('[GroupVulns] exploreGroup payload:', payload);

  dispatch({
    type: GROUP_VULNS_EXPLORE_OPEN,
    payload
  });

  if (ws) {
    let targetGroupId = 'new';
    if (groupId !== null && typeof groupId !== 'undefined') targetGroupId = groupId;
    else if (groupHeaderId !== null && typeof groupHeaderId !== 'undefined') targetGroupId = groupHeaderId;
    dispatch(redirect(`/vulns/${ws}/groups/${targetGroupId}`));
  }
};

export const closeExploreGroup = () => ({
  type: GROUP_VULNS_EXPLORE_CLOSE
});

export const setExploreGroupActiveTab = (tab) => ({
  type: GROUP_VULNS_EXPLORE_SET_ACTIVE_TAB,
  tab
});

export const setExploreGroupHostsPage = (page = DEFAULT_EXPLORE_PAGE) => ({
  type: GROUP_VULNS_EXPLORE_SET_HOSTS_PAGE,
  page: normalizePositiveInteger(page, DEFAULT_EXPLORE_PAGE)
});

export const setExploreGroupServicesPage = (page = DEFAULT_EXPLORE_PAGE) => ({
  type: GROUP_VULNS_EXPLORE_SET_SERVICES_PAGE,
  page: normalizePositiveInteger(page, DEFAULT_EXPLORE_PAGE)
});

export const showUngroupConfirmationModal = () => ({
  type: GROUP_VULNS_EXPLORE_SHOW_UNGROUP_CONFIRMATION_MODAL
});

export const hideUngroupConfirmationModal = () => ({
  type: GROUP_VULNS_EXPLORE_HIDE_UNGROUP_CONFIRMATION_MODAL
});

export const setExploreGroupSelectedHostIds = (ids) => ({
  type: GROUP_VULNS_EXPLORE_SET_SELECTED_HOST_IDS,
  ids: Array.isArray(ids) ? ids : []
});

export const setExploreGroupSelectedServiceIds = (ids) => ({
  type: GROUP_VULNS_EXPLORE_SET_SELECTED_SERVICE_IDS,
  ids: Array.isArray(ids) ? ids : []
});

const updateExploreHostTagsLocally = (hostIds, tagsToAdd, tagsToRemove) => ({
  type: GROUP_VULNS_EXPLORE_UPDATE_HOST_TAGS,
  hostIds,
  tagsToAdd,
  tagsToRemove
});

const updateExploreServiceTagsLocally = (serviceIds, tagsToAdd, tagsToRemove) => ({
  type: GROUP_VULNS_EXPLORE_UPDATE_SERVICE_TAGS,
  serviceIds,
  tagsToAdd,
  tagsToRemove
});

const applyExploreHostTags = (tagsToAdd, tagsToRemove) => async (dispatch, getState) => {
  const state = getState();
  const ws = selectCurrentWorkspace(state);
  const ids = state.groupVulns?.explore?.selectedHostIds ?? [];
  if (!ws || !ids.length) return;
  const numericIds = ids.map(Number).filter((n) => !Number.isNaN(n));
  dispatch(updateExploreHostTagsLocally(ids, tagsToAdd, tagsToRemove));
  try {
    await api.host.setHostsTags(ws, { host_ids: numericIds, tags_to_add: tagsToAdd, tags_to_remove: tagsToRemove });
    dispatch(getTags());
    dispatch(getHostsTags());
  } catch (e) {
    dispatch(updateExploreHostTagsLocally(ids, tagsToRemove, tagsToAdd));
  }
};

const applyExploreServiceTags = (tagsToAdd, tagsToRemove) => async (dispatch, getState) => {
  const state = getState();
  const ws = selectCurrentWorkspace(state);
  const ids = state.groupVulns?.explore?.selectedServiceIds ?? [];
  if (!ws || !ids.length) return;
  const numericIds = ids.map(Number).filter((n) => !Number.isNaN(n));
  dispatch(updateExploreServiceTagsLocally(ids, tagsToAdd, tagsToRemove));
  try {
    await api.service.setServicesTags(ws, { service_ids: numericIds, tags_to_add: tagsToAdd, tags_to_remove: tagsToRemove });
    dispatch(getTags());
    dispatch(getHostsTags());
    dispatch(getServicesTags());
  } catch (e) {
    dispatch(updateExploreServiceTagsLocally(ids, tagsToRemove, tagsToAdd));
  }
};

export const addExploreGroupHostTag = (tag) => applyExploreHostTags([tag], []);
export const removeExploreGroupHostTag = (tag) => applyExploreHostTags([], [tag]);
export const setExploreGroupHostNewTags = (newTags) => applyExploreHostTags(newTags, []);

export const addExploreGroupServiceTag = (tag) => applyExploreServiceTags([tag], []);
export const removeExploreGroupServiceTag = (tag) => applyExploreServiceTags([], [tag]);
export const setExploreGroupServiceNewTags = (newTags) => applyExploreServiceTags(newTags, []);

export const deleteExploreGroupSelectedHosts = () => async (dispatch, getState) => {
  const state = getState();
  const ws = selectCurrentWorkspace(state);
  const ids = state.groupVulns?.explore?.selectedHostIds ?? [];
  if (!ws || !ids.length) return;
  const numericIds = ids.map(Number).filter((n) => !Number.isNaN(n));
  try {
    await api.host.deleteHost(ws, numericIds);
    dispatch({ type: GROUP_VULNS_EXPLORE_HOSTS_DELETE_SUCCESS, hostIds: ids });
    dispatch(refreshExploreGroupHosts());
  } catch (e) {
    dispatch(fail(e));
  }
};

export const deleteExploreGroupSelectedServices = () => async (dispatch, getState) => {
  const state = getState();
  const ws = selectCurrentWorkspace(state);
  const ids = state.groupVulns?.explore?.selectedServiceIds ?? [];
  if (!ws || !ids.length) return;
  const numericIds = ids.map(Number).filter((n) => !Number.isNaN(n));
  try {
    await api.service.deleteService(ws, numericIds);
    dispatch({ type: GROUP_VULNS_EXPLORE_SERVICES_DELETE_SUCCESS, serviceIds: ids });
    dispatch(refreshExploreGroupServices());
  } catch (e) {
    dispatch(fail(e));
  }
};
